import { useMutation } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import { Button } from '@winnin/winnin_ui.components.button'
import { useToast } from '@winnin/winnin_ui.components.toast'
import { TooltipWrapper } from '@winnin/winnin_ui.components.tooltip-wrapper'

import { useWinninAIReport } from 'contexts/WinninAIReportContext'
import { customerioTracker } from 'lib'
import { T, useTranslate } from 'lib/tolgee'
import { UserStore } from 'stores'
import { analyticsTracker, TOLGEE_GLOBALS } from 'utils'

import { CREATE_WEEKLY_AGENDA_REPORT, CREATE_TREND_ANALYSIS_REPORT } from './query'

const TOLGEE_PREFIX = 'GenerateReportButton'

function GenerateReportButton({ reportType, reportLanguage, isNotEligible, loading, isBlocked }) {
  const history = useHistory()
  const { winninAIReport, resetWinninAIReport } = useWinninAIReport()
  const { createToast } = useToast()

  const t = useTranslate()

  function handleReportError(error) {
    if (error?.message === 'insufficient-funds') {
      createToast({
        content: t({
          key: `${TOLGEE_PREFIX}-insuficientFundsToast`,
          defaultValue:
            'Ops! Você não tem créditos disponíveis para criar esse Winnin Intelligence Report. Entre em contato com nossos consultores.',
        }),
      })
      return
    }

    createToast({
      content: t({
        key: `${TOLGEE_PREFIX}-errorToast`,
        defaultValue: 'Ops! Tivemos um erro na geração do seu Winnin Intelligence Report. Tente novamente.',
      }),
    })
  }

  function handleReportCompletion(report) {
    resetWinninAIReport()

    if (report?.success === false && report?.message === 'insufficient-funds') {
      createToast({
        content: t({
          key: `${TOLGEE_PREFIX}-insuficientFundsToast`,
          defaultValue:
            'Ops! Você não tem créditos disponíveis para criar esse Winnin Intelligence Report. Entre em contato com nossos consultores.',
        }),
      })
      return
    }

    if (report?.id && report?.workspaceId) {
      history.push(`/workspace/${report?.workspaceId}/jobs/${report?.id}/dashboard`)
    }
  }

  const [generateWeeklyAgendaReport, { loading: weeklyAgendaLoading }] = useMutation(CREATE_WEEKLY_AGENDA_REPORT, {
    onCompleted: ({ createAutomaticJobWeeklySchedule }) => {
      handleReportCompletion(createAutomaticJobWeeklySchedule)
    },
    onError: error => {
      console.error('Error @ CREATE_WEEKLY_AGENDA_REPORT >>>>>>', error)

      handleReportError(error)
    },
  })

  const [generateTrendAnalysisReport, { loading: trendLoading }] = useMutation(CREATE_TREND_ANALYSIS_REPORT, {
    onCompleted: ({ createAutomaticJobTrendAnalysis }) => {
      handleReportCompletion(createAutomaticJobTrendAnalysis)
    },
    onError: error => {
      console.error('Error @ CREATE_TREND_ANALYSIS_REPORT >>>>>>', error)

      handleReportError(error)
    },
  })

  const generateReport = {
    trend_analysis: generateTrendAnalysisReport,
    weekly_schedule: generateWeeklyAgendaReport,
  }

  const generateReportLoading = {
    trend_analysis: trendLoading,
    weekly_schedule: weeklyAgendaLoading,
  }

  async function handleGenerateReportButtonClick(ignoreCharge = false) {
    const outputProviders = winninAIReport?.outputProviders
    const generateZaiReport = generateReport[reportType]

    const generatedReport = await generateZaiReport({
      variables: {
        data: {
          ...(ignoreCharge === true
            ? {
                chargeOptions: {
                  ignoreChargeOnCurrentWorkspace: true,
                },
              }
            : {}),
          territoryId: `${winninAIReport?.entity}.${winninAIReport?.id}`,
          ...(outputProviders ? { outputProviders } : {}),
          outputLanguage: reportLanguage,
        },
      },
    })

    const jobId =
      generatedReport?.data?.createAutomaticJobTrendAnalysis?.id ||
      generatedReport?.data?.createAutomaticJobWeeklySchedule?.id

    const { CREATED_REPORT } = customerioTracker.eventNames
    customerioTracker.track(CREATED_REPORT, {
      id: winninAIReport?.id,
      job_id: jobId,
      type: reportType,
    })

    analyticsTracker.eventTrack('AI Report Modal Generate Click', {
      id: winninAIReport?.id,
      entity: winninAIReport?.entity,
      type: reportType,
      ignoreCharge: !!ignoreCharge,
      jobId,
    })
  }

  const isWinner = UserStore?.userInfo?.company?.id === 'WINNIN'

  return (
    <>
      <TooltipWrapper
        tooltipMessage={
          !!isNotEligible && !loading
            ? t({
                ...TOLGEE_GLOBALS.notEligibleWinninAIReport,
                parameters: { type: reportType },
              })
            : ''
        }
        tooltipStyle="tooltippedN"
        isTooltipMultiline
      >
        <Button
          onClick={handleGenerateReportButtonClick}
          disabled={isBlocked || generateReportLoading[reportType] || isNotEligible || loading}
        >
          <T keyName={`${TOLGEE_PREFIX}-generateReport`}>Gerar report</T>
        </Button>
      </TooltipWrapper>

      {isWinner && (
        <Button
          onClick={() => handleGenerateReportButtonClick(true)}
          disabled={generateReportLoading[reportType] || isNotEligible || loading}
        >
          <T keyName={TOLGEE_GLOBALS.generateFreeReport.key}>{TOLGEE_GLOBALS.generateFreeReport.defaultValue}</T>
        </Button>
      )}
    </>
  )
}

export default GenerateReportButton
